import { setContext, withScope } from '@sentry/react';
import { ResumedLeadState, SignupDirectContext } from '../context';
import { EvaluationFormType } from '../schemas/evaluationSchema';
import { FieldErrors } from 'react-hook-form';
import { SENTRY_CONSTANTS, SentryIssueReportType } from 'utils/sentry';

const SIGNUP_ENTIRE_CONTEXT_NAME = 'Signup app context (general)';
const enum SignupSentryContextKeys {
  Filters = 'signup-filters',
  EvalTimeSlot = 'signup-eval-time-slot',
  IncompleteEvalForm = 'signup-incomplete-eval-form',
  ResumedLeadState = 'signup-resumed-lead-state',
}

const RESUMED_LEAD_CONTEXT_NAME = 'Resumed lead context';

const enum ResumedLeadSentryContextKeys {
  isResumedLead = 'Is resumed lead',
  preApprovedEligibility = 'Has pre approved eligibility',
  prefilledAppointment = 'Has prefilled Appointment',
}

/** Takes the entire signup context and returns a reduced object with the critical information for Sentry */
export const setupSignupSentryContext = (ctx: SignupDirectContext, resumedLeadState?: ResumedLeadState) => {
  setContext(RESUMED_LEAD_CONTEXT_NAME, {
    [ResumedLeadSentryContextKeys.isResumedLead]: resumedLeadState !== undefined,
    [ResumedLeadSentryContextKeys.preApprovedEligibility]: resumedLeadState?.shouldSkipEligibility ?? false,
    [ResumedLeadSentryContextKeys.prefilledAppointment]: resumedLeadState?.prefilledAppointment !== undefined,
  });

  setContext(SIGNUP_ENTIRE_CONTEXT_NAME, {
    [SignupSentryContextKeys.Filters]: ctx.filters,
    [SignupSentryContextKeys.EvalTimeSlot]: ctx.evaluationSchedule,
    [SignupSentryContextKeys.IncompleteEvalForm]: ctx.incompleteEvalForm,
    [SignupSentryContextKeys.ResumedLeadState]: ctx.resumedLeadState,
  });
};

const EVAL_FORM_CONTEXT_NAME = 'Signup Evaluation Form';

const enum SignupFormWarningContextKeys {
  EvalAttempts = 'Submit attempts count',
  FormErrors = 'Form field errors',
  SubmissionAttemptData = 'Submission attempt data',
}

const SUBMIT_WARNING_SUBMIT_THRESHOLD = 3;

export const reportMultipleInvalidSignupEvalForm = (
  formErrors: FieldErrors<EvaluationFormType>,
  attempts: number,
  incompleteForm: DeepPartial<EvaluationFormType>,
) => {
  if (attempts < SUBMIT_WARNING_SUBMIT_THRESHOLD) return;

  withScope(scope => {
    scope.setContext(EVAL_FORM_CONTEXT_NAME, {
      [SignupFormWarningContextKeys.EvalAttempts]: attempts,
      [SignupFormWarningContextKeys.FormErrors]: formErrors,
      [SignupFormWarningContextKeys.SubmissionAttemptData]: incompleteForm,
    });
    scope.setTag(SENTRY_CONSTANTS.SENTRY_ISSUE_REPORT_TYPE_TAG_KEY, SentryIssueReportType.softError);
    scope.captureMessage('Multiple invalid signup eval form', 'warning');
  });
};
